import { useStore } from 'react-redux';
import { DateTime } from 'luxon';
import { LogService } from '../../../services/log-service';

import { capabilitiesRegistry } from './capabilities-registry';

/**
 * @typedef {object} CapabilityContextHook
 * @property {(capabilityId: string) => object | null} buildContext - Build context for a capability
 * @property {() => Record<string, object>} buildAllContexts - Build contexts for all capabilities
 */

/**
 * Hook that provides access to Redux state for capability context builders.
 * Uses useStore() to take a state snapshot at call time rather than subscribing
 * to individual selectors — contexts are only needed when sending a message,
 * not reactively on every state change.
 * @returns {CapabilityContextHook} Context building methods
 */
export const useCapabilityContext = () => {
  const store = useStore();

  /**
   * Build context for a specific capability
   * @param {string} capabilityId - Capability ID to build context for
   * @returns {object | null} The capability context or null if no context builder exists
   */
  const buildContext = (capabilityId) => {
    const capability = capabilitiesRegistry.capabilities[capabilityId];

    if (!capability?.getAdditionalContext) {
      return null;
    }

    try {
      const state = store.getState();

      // Safely parse account created date for order search context
      let accountCreatedDateUtc = null;
      const sellerCreateDateUTC = state?.seller?.data?.createDateUTC;
      if (sellerCreateDateUTC) {
        const parsed = DateTime.fromISO(sellerCreateDateUTC, { zone: 'utc' });
        if (parsed.isValid) {
          accountCreatedDateUtc = parsed.toISODate();
        }
      }

      // Augment state with runtime-computed values for date calculations
      const augmentedState = {
        ...state,
        currentDateUtc: DateTime.utc().toISODate(),
        accountCreatedDateUtc,
      };

      return capability.getAdditionalContext(augmentedState);
    } catch (error) {
      LogService.logError(new Error('Failed to get additional context for capability', { cause: error }), { capabilityId });
      return null;
    }
  };

  /**
   * Build contexts for all capabilities that have context builders
   * @returns {Record<string, object>} Map of capability IDs to their contexts
   */
  const buildAllContexts = () => {
    const contexts = {};

    Object.keys(capabilitiesRegistry.capabilities).forEach((capabilityId) => {
      const context = buildContext(capabilityId);
      if (context) {
        contexts[capabilityId] = context;
      }
    });

    return contexts;
  };

  return { buildContext, buildAllContexts };
};
